import { create } from 'zustand';

/**
 * Short messages about what just happened. Request of 5 October 2026,
 * section 5: they stack neatly (three at most, the newest nearest), stay for
 * about three and a half seconds, can be swiped away, and can carry one action
 * such as Undo.
 *
 * A toast is never red. Red means urgent, section 12: an error is a calm dark
 * toast with a warning mark, not a red flash.
 */
export type ToastTone = 'neutral' | 'success' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  action: ToastAction | null;
  /** How long it stays, when it is not the usual time; null for the usual. */
  durationMs: number | null;
}

export interface ToastOptions {
  tone?: ToastTone;
  action?: ToastAction;
  /**
   * How long it stays, in milliseconds, instead of the usual time. For the one
   * toast that has a reason to: Undo is offered for about ten seconds, request
   * of 5 October 2026, section 6.
   */
  durationMs?: number;
}

/** How many are on screen at once; an older one gives way to a newer. */
export const MAX_TOASTS = 3;

interface ToastState {
  items: ToastItem[];
  /** The newest message, or null when none is showing. */
  message: string | null;
  /** Incremented on every show, so the same message twice still re-announces. */
  nonce: number;
  show: (message: string, options?: ToastOptions) => number;
  /** One toast, or all of them. */
  dismiss: (id?: number) => void;
}

export const useToastStore = create<ToastState>((set) => ({
  items: [],
  message: null,
  nonce: 0,
  show: (message, options = {}) => {
    let id = 0;
    set((state) => {
      id = state.nonce + 1;
      // The same words again replace the earlier toast rather than stacking a copy.
      const others = state.items.filter((item) => item.message !== message);
      const items = [
        ...others,
        {
          id,
          message,
          tone: options.tone ?? 'neutral',
          action: options.action ?? null,
          durationMs: options.durationMs ?? null,
        },
      ].slice(-MAX_TOASTS);
      return { items, message, nonce: id };
    });
    return id;
  },
  dismiss: (id) =>
    set((state) => {
      const items = id === undefined ? [] : state.items.filter((item) => item.id !== id);
      return { items, message: items.at(-1)?.message ?? null };
    }),
}));

/** Callable from outside React, which is what an error handler in a repository needs. */
export function toast(message: string, options?: ToastOptions): void {
  useToastStore.getState().show(message, options);
}
